import type { ScheduledJob } from '../../api/monitor';
import { Card } from '../../components/ui/Card';
import { formatCheckedAt } from '../../lib/health';
import { RUN_TONE, cronLabel, formatDuration, formatNextRun } from '../../lib/serviceRuns';

interface JobCardProps {
  job: ScheduledJob;
  /** Este job está sendo disparado pela tela agora. */
  triggering: boolean;
  onRunNow: (job: ScheduledJob) => void;
  /** Filtra o histórico de baixo por este job. */
  onSeeRuns: (job: ScheduledJob) => void;
}

/**
 * Resumo da última rodada: quando foi, quanto durou e quanto processou.
 *
 * Um job que nunca rodou não tem o que resumir, e dizer isso é mais útil do que
 * três traços em sequência — é justamente o caso que interessa depois de subir
 * um serviço novo.
 */
function lastRunSummary(job: ScheduledJob): string {
  const run = job.lastRun;

  if (!run) return 'Nunca executado neste ambiente.';
  if (run.status === 'running') {
    return job.running ? 'Em execução agora…' : 'Interrompida: o serviço reiniciou durante a rodada.';
  }

  const parts = [`duração ${formatDuration(run.durationMs)}`, `${run.processed} processado(s)`];

  if (run.failed > 0) parts.push(`${run.failed} com falha`);
  if (run.triggeredBy === 'manual') parts.push('disparo manual');
  if (run.triggeredBy === 'queue') parts.push('publicação recebida');

  return parts.join(' · ');
}

/**
 * A linha de frequência.
 *
 * O job de cron promete um horário e a próxima execução; o de fila não promete
 * nada, porque quem decide quando ele roda é o outro sistema. Escrever "sem
 * próxima execução" ali daria a entender que alguma coisa está desligada, e o
 * que acontece é o contrário: está funcionando, só não tem hora marcada.
 */
function scheduleSummary(job: ScheduledJob): string {
  if (job.kind === 'queue') {
    return job.scheduled
      ? 'Origem: publicação do SGC · sem hora marcada'
      : 'Origem: publicação do SGC · fila indisponível';
  }

  return `Frequência: ${cronLabel(job.schedule)} · ${
    job.scheduled ? `próxima ${formatNextRun(job.nextRunAt)}` : 'não agendado'
  }`;
}

/**
 * Um job agendado: o que faz, quando roda, como terminou da última vez e o
 * botão para rodar agora.
 *
 * O cadastro de agendamentos e `service_runs` respondem coisas diferentes — o
 * primeiro sabe o que *deveria* rodar, o segundo o que rodou —, e é o
 * cruzamento dos dois que revela "está agendado e nunca executou".
 */
export function JobCard({ job, triggering, onRunNow, onSeeRuns }: JobCardProps) {
  const { lastRun } = job;
  // `job.running` já descarta rodada presa por serviço que morreu; usar
  // `lastRun.status` aqui deixaria o botão travado até a faxina passar.
  const running = job.running || triggering;

  const tone = running
    ? RUN_TONE.running
    : lastRun
      ? RUN_TONE[lastRun.status]
      : { label: 'Nunca executado', pill: 'bg-surface-200 text-ink-600' };

  return (
    <Card>
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-extrabold">{job.label}</span>
            <span className="bg-surface-200 text-ink-600 rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase">
              {job.service}
            </span>
          </div>

          <div className="text-ink-600 mt-0.5 text-[12px] leading-normal">{job.description}</div>

          <div className="text-ink-400 mt-1.5 text-[11.5px]">
            Última execução:{' '}
            <b className="text-ink-600">
              {lastRun ? formatCheckedAt(lastRun.startedAt) : 'nunca'}
            </b>{' '}
            · {lastRunSummary(job)}
          </div>

          <div className="text-ink-400 mt-0.5 text-[11.5px]">{scheduleSummary(job)}</div>
        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${tone.pill}`}
        >
          {tone.label}
        </span>
      </div>

      {/* A rodada não reporta progresso — o eagle leva minutos e só avisa no fim —,
          então a barra é indeterminada: ela diz "está andando", não "quanto falta". */}
      {running ? (
        <div className="bg-surface-200 mt-3 h-1.5 overflow-hidden rounded-full">
          <div className="bg-navy-800 animate-progress h-full w-1/3 rounded-full" />
        </div>
      ) : null}

      {job.unscheduledReason ? (
        <div className="border-warn-200 bg-warn-100 text-warn-700 mt-2.5 rounded-[10px] border px-3 py-2 text-[11.5px] font-semibold">
          {job.unscheduledReason}
        </div>
      ) : null}

      {lastRun?.error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mt-2.5 rounded-[10px] border px-3 py-2 text-[11.5px] font-semibold">
          {lastRun.error}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onRunNow(job)}
          disabled={running || !job.triggerable}
          title={
            running
              ? 'A rodada anterior ainda está em execução.'
              : job.triggerable
                ? undefined
                : job.kind === 'queue'
                  ? 'Esta rodada acontece quando o SGC publica o snapshot; não há o que disparar daqui.'
                  : `Quem executa esta rodada é o ${job.service}.`
          }
          className="border-line-300 text-navy-800 hover:border-navy-800 hover:bg-surface-100 disabled:hover:border-line-300 disabled:hover:bg-white rounded-[10px] border bg-white px-4 py-2.5 text-[13px] font-extrabold disabled:opacity-50"
        >
          {running ? 'Executando…' : 'Executar agora'}
        </button>

        <button
          type="button"
          onClick={() => onSeeRuns(job)}
          className="text-navy-800 px-1 text-[12.5px] font-extrabold hover:underline"
        >
          Ver execuções
        </button>
      </div>
    </Card>
  );
}
